const normalizeId = (value) => String(value ?? '').split('/').pop();

class DjProductBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.form = this.querySelector('[data-bundle-form]');
    this.submitButton = this.querySelector('[data-bundle-submit]');
    this.mainItem = this.querySelector('[data-bundle-item][data-required="true"]');
    this.productId = normalizeId(this.dataset.productId);
    this.discountFactor = (100 - Number(this.dataset.discountPercent || 5)) / 100;
    this.offerId = this.dataset.offerId?.trim();
    this.abortController = new AbortController();
    this.mainVariantAvailable = true;

    try {
      this.mainVariants = JSON.parse(this.querySelector('[data-main-variants]')?.textContent || '[]');
    } catch (error) {
      console.warn('Djurslottet produktpaket: kunde inte läsa huvudproduktens varianter.', error);
      this.mainVariants = [];
    }

    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'SEK';
    try {
      this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'sv-SE', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    } catch (_error) {
      this.moneyFormatter = null;
    }

    const { signal } = this.abortController;
    this.addEventListener('change', this.handleChange, { signal });
    this.addEventListener('click', this.handleClick, { signal });
    this.form?.addEventListener('submit', this.handleSubmit, { signal });
    document.addEventListener('variant:update', this.handleMainVariantUpdate, { signal });

    for (const track of this.querySelectorAll('[data-info-track]')) {
      track.addEventListener('scroll', () => this.scheduleGalleryStatus(track), { signal, passive: true });
    }

    const variantFromUrl = new URL(window.location.href).searchParams.get('variant');
    if (variantFromUrl) this.setMainVariant(variantFromUrl);

    this.updateBundle();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.removeAttribute('data-initialized');
  }

  handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    const item = target.closest('[data-bundle-item]');
    if (!item) return;

    if (target.matches('[data-bundle-select]')) {
      const option = target.selectedOptions[0];
      const variantInput = item.querySelector('[data-bundle-variant]');
      const image = item.querySelector('[data-bundle-image]');

      item.dataset.price = option?.dataset.bundlePrice || item.dataset.price;
      item.dataset.regularPrice = option?.dataset.bundleRegularPrice || item.dataset.price;
      if (variantInput && option?.value) variantInput.value = option.value;
      if (image && option?.dataset.image) image.src = option.dataset.image;
    }

    this.updateBundle();
  };

  handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openButton = target.closest('[data-info-open]');
    if (openButton) {
      const dialogId = openButton.getAttribute('aria-controls');
      const dialog = dialogId ? this.querySelector(`#${CSS.escape(dialogId)}`) : null;
      if (dialog instanceof HTMLDialogElement) {
        this.activeInfoTrigger = openButton;
        dialog.showModal();
        const track = dialog.querySelector('[data-info-track]');
        if (track instanceof HTMLElement) {
          track.scrollTo({ left: 0, behavior: 'auto' });
          this.updateGalleryStatus(track);
        }
      }
      return;
    }

    const galleryButton = target.closest('[data-info-prev], [data-info-next]');
    if (galleryButton instanceof HTMLButtonElement) {
      this.moveGallery(galleryButton, galleryButton.matches('[data-info-next]') ? 1 : -1);
      return;
    }

    const closeButton = target.closest('[data-info-close]');
    const dialog = target.closest('[data-info-dialog]');
    if (dialog instanceof HTMLDialogElement && (closeButton || target === dialog)) {
      dialog.close();
      this.activeInfoTrigger?.focus();
    }
  };

  moveGallery(button, direction) {
    const dialog = button.closest('[data-info-dialog]');
    const track = dialog?.querySelector('[data-info-track]');
    if (!(track instanceof HTMLElement)) return;

    const slides = [...track.querySelectorAll('[data-info-slide]')];
    if (!slides.length) return;

    const currentIndex = this.getGalleryIndex(track, slides);
    const nextIndex = Math.max(0, Math.min(slides.length - 1, currentIndex + direction));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: slides[nextIndex].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.updateGalleryStatus(track, nextIndex);
  }

  scheduleGalleryStatus(track) {
    cancelAnimationFrame(track._djGalleryFrame);
    track._djGalleryFrame = requestAnimationFrame(() => this.updateGalleryStatus(track));
  }

  getGalleryIndex(track, slides = [...track.querySelectorAll('[data-info-slide]')]) {
    if (!slides.length) return 0;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  }

  updateGalleryStatus(track, forcedIndex = null) {
    const dialog = track.closest('[data-info-dialog]');
    const slides = [...track.querySelectorAll('[data-info-slide]')];
    if (!slides.length || !dialog) return;

    const index = forcedIndex ?? this.getGalleryIndex(track, slides);
    const indexNode = dialog.querySelector('[data-info-index]');
    const previousButton = dialog.querySelector('[data-info-prev]');
    const nextButton = dialog.querySelector('[data-info-next]');

    if (indexNode) indexNode.textContent = String(index + 1);
    if (previousButton instanceof HTMLButtonElement) previousButton.disabled = index <= 0;
    if (nextButton instanceof HTMLButtonElement) nextButton.disabled = index >= slides.length - 1;
  }

  handleMainVariantUpdate = (event) => {
    const eventProductId = normalizeId(event.detail?.data?.productId);
    if (eventProductId && eventProductId !== this.productId) return;

    const variantId = normalizeId(event.detail?.resource?.id);
    if (!variantId) {
      this.mainVariantAvailable = false;
      this.updateBundle();
      return;
    }

    this.setMainVariant(variantId, event.detail?.resource);
    this.updateBundle();
  };

  setMainVariant(variantId, eventVariant = null) {
    const normalizedVariantId = normalizeId(variantId);
    const storedVariant = this.mainVariants.find((variant) => normalizeId(variant.id) === normalizedVariantId);
    const variant = storedVariant || eventVariant;
    if (!variant || !this.mainItem) return;

    const variantInput = this.mainItem.querySelector('[data-bundle-variant]');
    const image = this.mainItem.querySelector('[data-bundle-image]');
    const title = this.mainItem.querySelector('[data-main-variant-title]');
    const imageUrl = variant.image || variant.featured_media?.preview_image?.src;

    if (variantInput) variantInput.value = normalizedVariantId;
    if (variant.price != null) {
      this.mainItem.dataset.price = String(variant.price);
      this.mainItem.dataset.regularPrice = String(variant.regular_price ?? variant.price);
    }
    if (image && imageUrl) image.src = imageUrl;
    if (title && variant.title) title.textContent = variant.title;

    this.mainVariantAvailable = variant.available !== false;
  }

  updateBundle() {
    const items = [...this.querySelectorAll('[data-bundle-item]')];
    const selectedItems = items.filter((item) => {
      if (item.dataset.required === 'true') return true;
      return item.querySelector('[data-bundle-toggle]')?.checked === true;
    });
    const hasBundleDiscount = selectedItems.length >= 2;
    const discountPercent = Number(this.dataset.discountPercent || 5);

    this.classList.toggle('is-single', !hasBundleDiscount);

    for (const item of items) {
      const selected = selectedItems.includes(item);
      const salePrice = Number(item.dataset.price || 0);
      const regularValue = Number(item.dataset.regularPrice || salePrice);
      const regularValueSafe = Math.max(salePrice, regularValue);
      const regularPrice = item.querySelector('[data-regular-price]');
      const discountedPrice = item.querySelector('[data-discounted-price]');
      const variantInput = item.querySelector('[data-bundle-variant]');
      const quantityInput = item.querySelector('[data-bundle-quantity]');
      const discountTag = item.querySelector('[data-discount-tag]');

      item.classList.toggle('is-excluded', !selected);
      item.setAttribute('aria-disabled', selected ? 'false' : 'true');
      if (regularPrice) regularPrice.textContent = this.formatMoney(regularValueSafe);
      if (discountedPrice) {
        discountedPrice.textContent = this.formatMoney(
          hasBundleDiscount ? Math.round(salePrice * this.discountFactor) : salePrice,
        );
      }
      if (discountTag) discountTag.textContent = `${discountPercent} % rabatt`;
      if (variantInput) variantInput.disabled = !selected;
      if (quantityInput) quantityInput.disabled = !selected;
    }

    const regularTotal = selectedItems.reduce((sum, item) => {
      const salePrice = Number(item.dataset.price || 0);
      return sum + Math.max(salePrice, Number(item.dataset.regularPrice || salePrice));
    }, 0);
    const saleTotal = selectedItems.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);
    const bundleTotal = hasBundleDiscount ? Math.round(saleTotal * this.discountFactor) : saleTotal;
    const savings = Math.max(0, regularTotal - bundleTotal);

    const originalTotalNode = this.querySelector('[data-original-total]');
    const bundleTotalNode = this.querySelector('[data-bundle-total]');
    const summaryMessage = this.querySelector('[data-summary-message]');

    if (originalTotalNode) originalTotalNode.textContent = this.formatMoney(regularTotal);
    if (bundleTotalNode) bundleTotalNode.textContent = this.formatMoney(bundleTotal);
    if (summaryMessage) {
      if (hasBundleDiscount) {
        const template = summaryMessage.dataset.activeTemplate || 'Du sparar [belopp] när du köper tillsammans.';
        this.renderSummaryMessage(summaryMessage, template, savings);
      } else {
        summaryMessage.textContent = summaryMessage.dataset.singleMessage || 'Välj minst en produkt till för att få paketpris.';
      }
    }

    if (this.submitButton) this.submitButton.disabled = !this.mainVariantAvailable;
  }

  renderSummaryMessage(container, template, savings) {
    const placeholder = '[belopp]';
    const placeholderIndex = template.indexOf(placeholder);
    if (placeholderIndex === -1) {
      container.textContent = template;
      return;
    }

    const amount = document.createElement('strong');
    amount.dataset.savingsAmount = '';
    amount.textContent = this.formatMoney(savings);
    container.replaceChildren(
      document.createTextNode(template.slice(0, placeholderIndex)),
      amount,
      document.createTextNode(template.slice(placeholderIndex + placeholder.length)),
    );
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    const selectedItems = [...this.querySelectorAll('[data-bundle-item]')].filter((item) => {
      if (item.dataset.required === 'true') return true;
      return item.querySelector('[data-bundle-toggle]')?.checked === true;
    });

    const items = selectedItems.map((item, index) => {
      const variantInput = item.querySelector('[data-bundle-variant]');
      const quantityInput = item.querySelector('[data-bundle-quantity]');
      if (variantInput) {
        variantInput.disabled = false;
        variantInput.name = `items[${index}][id]`;
      }
      if (quantityInput) {
        quantityInput.disabled = false;
        quantityInput.name = `items[${index}][quantity]`;
      }
      const cartItem = {
        id: Number(variantInput?.value),
        quantity: Number(quantityInput?.value || 1),
      };
      if (this.offerId) {
        cartItem.properties = {
          '_lb-product': `${this.productId}#${this.offerId}`,
        };
      }
      return cartItem;
    });

    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
    }

    const errorMessage = this.querySelector('[data-bundle-error]');
    if (errorMessage) errorMessage.hidden = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Paketet kunde inte läggas i varukorgen.');
      }

      const discountCode = this.dataset.discountCode?.trim();
      const checkoutUrl =
        selectedItems.length >= 2 && discountCode
          ? `/discount/${encodeURIComponent(discountCode)}?redirect=/checkout`
          : '/checkout';

      window.location.assign(checkoutUrl);
    } catch (error) {
      console.error('Djurslottet produktpaket:', error);
      if (errorMessage) {
        errorMessage.textContent = error.message || 'Något gick fel. Försök igen.';
        errorMessage.hidden = false;
      }
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-busy');
      }
    }
  };

  formatMoney(cents) {
    const roundedAmount = Math.round(Number(cents || 0) / 100);
    return this.moneyFormatter?.format(roundedAmount) || `${roundedAmount} kr`;
  }
}

if (!customElements.get('dj-product-bundle')) {
  customElements.define('dj-product-bundle', DjProductBundle);
}
